import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import type { RootState } from '../store';
import { useAuth } from '../hooks/useAuth';

interface PrivateRouteProps {
  children: ReactNode;
}

// Keep auth routes absolute to avoid basename/redirect loops
const LOGIN_ROUTE = '/login';
const AUTH_PREFIX = '/auth/';

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children }) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => (state as any).auth);
  const { checkAuth, isLoading } = useAuth();
  const location = useLocation();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const path = location.pathname;
  // Be permissive on auth/login routes to avoid redirect loops even if base paths or prefixes are injected by hosting
  const isCallbackRoute = path.includes(AUTH_PREFIX);
  const isLoginRoute = path.includes('/login');
  // Debug escape hatch: allow disabling redirects via query or localStorage flag
  const search = location.search || '';
  const disableGuard =
    search.includes('authDebug=1') || (typeof localStorage !== 'undefined' && localStorage.getItem('disable_auth_guard') === 'true');

  useEffect(() => {
    if (!hasCheckedAuth && !isLoginRoute && !isCallbackRoute) {
      checkAuth().finally(() => setHasCheckedAuth(true));
    } else if (!hasCheckedAuth) {
      // Avoid blocking login/callback routes
      setHasCheckedAuth(true);
    }
  }, [hasCheckedAuth, checkAuth, isLoginRoute, isCallbackRoute]);

  // Debug trace
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[PrivateRoute]', {
      path,
      isAuthenticated,
      isLoginRoute,
      isCallbackRoute,
      roles: user?.roles,
      disableGuard,
    });
  }

  if (disableGuard) {
    return <>{children}</>;
  }

  // If on login or auth callback routes, allow rendering without redirect
  if (isLoginRoute || isCallbackRoute) {
    return <>{children}</>;
  }

  if (!hasCheckedAuth || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(path + location.search);
    // Use hard redirect to avoid React Router redirect loops
    if (typeof window !== 'undefined') {
      window.location.href = `${LOGIN_ROUTE}?returnUrl=${returnUrl}`;
      return null;
    }
    return <Navigate to={`${LOGIN_ROUTE}?returnUrl=${returnUrl}`} replace />;
  }

  return <>{children}</>;
};

export default PrivateRoute;
